/**
 * EntityAvatar Component
 *
 * Versatile avatar component for displaying:
 * - Single person avatar (with initials or photo)
 * - Multiple people (avatar stack)
 * - Company/Organization logo (with initials or logo image)
 *
 * Used as the second element in every Row, after PrimaryAction.
 */

import React, { useMemo } from 'react';
import { Building2, User } from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export type AvatarType = 'person' | 'company' | 'auto';

export interface AvatarItem {
  /** Unique identifier (user ID, contact ID, etc.) */
  id?: string;
  /** Display name for generating initials */
  name: string;
  /** Optional image URL */
  imageUrl?: string;
  /** Override type for this item */
  type?: 'person' | 'company';
}

export interface EntityAvatarProps {
  /** Single item or array of items to display */
  items: AvatarItem | AvatarItem[];
  /** Avatar type - affects icon and color scheme */
  type?: AvatarType;
  /** Size in pixels (default: 24) */
  size?: number;
  /** Maximum items to show before +N indicator (default: 3) */
  maxVisible?: number;
  /** Show placeholder when empty (default: true) */
  showPlaceholder?: boolean;
  /** Custom color override */
  color?: string;
  /** Click handler - receives the clicked item (or first item for stack) */
  onClick?: (item: AvatarItem) => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function EntityAvatar({
  items,
  type = 'auto',
  size = 24,
  maxVisible = 3,
  showPlaceholder = true,
  color,
  onClick,
}: EntityAvatarProps) {
  // Normalize to array
  const itemsArray = useMemo(() => {
    if (!items) return [];
    return Array.isArray(items) ? items : [items];
  }, [items]);

  // Auto-detect type from first item if needed
  const resolvedType = useMemo(() => {
    if (type !== 'auto') return type;
    if (itemsArray.length === 0) return 'person';
    const firstItem = itemsArray[0];
    if (firstItem.type) return firstItem.type;
    // Heuristic: if name contains common company indicators
    const name = firstItem.name.toLowerCase();
    if (name.includes('srl') || name.includes('spa') || name.includes('snc') ||
        name.includes('sas') || name.includes('ltd') || name.includes('inc') ||
        name.includes('gmbh') || name.includes('s.r.l') || name.includes('s.p.a')) {
      return 'company';
    }
    return 'person';
  }, [type, itemsArray]);

  // Empty state
  if (itemsArray.length === 0) {
    if (!showPlaceholder) return null;
    return <PlaceholderAvatar type={resolvedType} size={size} />;
  }

  // Single item
  if (itemsArray.length === 1) {
    return (
      <SingleAvatar
        item={itemsArray[0]}
        type={resolvedType}
        size={size}
        color={color}
        onClick={onClick}
      />
    );
  }

  // Multiple items - avatar stack
  return (
    <AvatarStack
      items={itemsArray}
      type={resolvedType}
      size={size}
      maxVisible={maxVisible}
      color={color}
      onClick={onClick}
    />
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Placeholder avatar when no items provided
 */
function PlaceholderAvatar({
  type,
  size,
}: {
  type: 'person' | 'company';
  size: number;
}) {
  const iconSize = Math.round(size * 0.5);

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: type === 'company' ? '4px' : '50%',
        background: '#e5e7eb',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {type === 'company' ? (
        <Building2 size={iconSize} color="#9ca3af" />
      ) : (
        <User size={iconSize} color="#9ca3af" />
      )}
    </div>
  );
}

/**
 * Single avatar display
 */
function SingleAvatar({
  item,
  type,
  size,
  color,
  onClick,
}: {
  item: AvatarItem;
  type: 'person' | 'company';
  size: number;
  color?: string;
  onClick?: (item: AvatarItem) => void;
}) {
  const initials = getInitials(item.name);
  const bgColor = color || generateColor(item.id || item.name, type);
  const isCompany = type === 'company';
  const fontSize = Math.round(size * 0.4);
  const isClickable = !!onClick;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.stopPropagation();
      onClick(item);
    }
  };

  const clickableStyle: React.CSSProperties = isClickable
    ? { cursor: 'pointer', transition: 'transform 0.1s, box-shadow 0.1s' }
    : {};

  // If image URL provided, show image
  if (item.imageUrl) {
    return (
      <div
        style={{
          width: size,
          height: size,
          borderRadius: isCompany ? '4px' : '50%',
          overflow: 'hidden',
          flexShrink: 0,
          ...clickableStyle,
        }}
        title={item.name}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (isClickable) {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
          }
        }}
        onMouseLeave={(e) => {
          if (isClickable) {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = 'none';
          }
        }}
      >
        <img
          src={item.imageUrl}
          alt={item.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      </div>
    );
  }

  // Show initials
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: isCompany ? '4px' : '50%',
        background: bgColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: `${fontSize}px`,
        fontWeight: 600,
        color: '#fff',
        flexShrink: 0,
        textTransform: 'uppercase',
        ...clickableStyle,
      }}
      title={item.name}
      onClick={handleClick}
      onMouseEnter={(e) => {
        if (isClickable) {
          e.currentTarget.style.transform = 'scale(1.1)';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
        }
      }}
      onMouseLeave={(e) => {
        if (isClickable) {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      {initials}
    </div>
  );
}

/**
 * Avatar stack for multiple items
 */
function AvatarStack({
  items,
  type,
  size,
  maxVisible,
  color,
  onClick,
}: {
  items: AvatarItem[];
  type: 'person' | 'company';
  size: number;
  maxVisible: number;
  color?: string;
  onClick?: (item: AvatarItem) => void;
}) {
  const visible = items.slice(0, maxVisible);
  const extra = items.length - maxVisible;
  const overlap = Math.round(size * 0.25);
  const fontSize = Math.round(size * 0.35);
  const isClickable = !!onClick;

  const handleClick = (e: React.MouseEvent, item: AvatarItem) => {
    if (onClick) {
      e.stopPropagation();
      onClick(item);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexShrink: 0,
      }}
    >
      {visible.map((item, i) => {
        const initials = getInitials(item.name);
        const bgColor = color || generateColor(item.id || item.name, type);
        const isCompany = (item.type || type) === 'company';

        return (
          <div
            key={item.id || `${item.name}-${i}`}
            style={{
              width: size,
              height: size,
              borderRadius: isCompany ? '4px' : '50%',
              border: '2px solid #fff',
              background: item.imageUrl ? 'transparent' : bgColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: `${fontSize}px`,
              fontWeight: 600,
              color: '#fff',
              marginLeft: i > 0 ? `-${overlap}px` : '0',
              zIndex: maxVisible - i,
              overflow: 'hidden',
              textTransform: 'uppercase',
              cursor: isClickable ? 'pointer' : 'default',
              transition: 'transform 0.1s, box-shadow 0.1s',
            }}
            title={item.name}
            onClick={(e) => handleClick(e, item)}
            onMouseEnter={(e) => {
              if (isClickable) {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.zIndex = '100';
              }
            }}
            onMouseLeave={(e) => {
              if (isClickable) {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.zIndex = String(maxVisible - i);
              }
            }}
          >
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt={item.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              initials
            )}
          </div>
        );
      })}

      {extra > 0 && (
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            border: '2px solid #fff',
            background: '#9ca3af',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `${fontSize}px`,
            fontWeight: 600,
            color: '#fff',
            marginLeft: `-${overlap}px`,
          }}
          title={`+${extra} altri`}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Generate initials from a name
 * - "Mario Rossi" -> "MR"
 * - "Acme Corporation SRL" -> "AC"
 * - "single" -> "SI"
 */
function getInitials(name: string): string {
  if (!name) return '??';

  // Remove common suffixes
  const cleaned = name
    .replace(/\b(srl|spa|snc|sas|ltd|inc|gmbh|s\.r\.l|s\.p\.a|corp|co)\b/gi, '')
    .trim();

  const words = cleaned.split(/\s+/).filter(Boolean);

  if (words.length === 0) return name.substring(0, 2).toUpperCase();
  if (words.length === 1) return words[0].substring(0, 2).toUpperCase();

  // Take first letter of first two words
  return (words[0][0] + words[1][0]).toUpperCase();
}

/**
 * Generate a consistent color based on a string
 * Companies get more muted colors, people get vibrant colors
 */
function generateColor(seed: string, type: 'person' | 'company'): string {
  if (!seed) return '#9ca3af';

  // Simple hash function
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Convert to hue (0-360)
  const hue = Math.abs(hash) % 360;

  // Companies get lower saturation (more corporate feel)
  // People get higher saturation (more colorful)
  const saturation = type === 'company' ? 45 : 55;
  const lightness = type === 'company' ? 45 : 50;

  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

// =============================================================================
// EXPORTS
// =============================================================================

export { getInitials, generateColor };
